import Api from '../../shared/Api';

export interface IProcountorInvoicePreviewTotals {
    TotalRowCount: number;
    TotalGroupCount: number;
    TotalAmount: number;
    DefaultSelectedRowCount: number;
    DefaultSelectedAmount: number;
}

export interface IProcountorInvoicePreviewRow {
    OrderRowId: number;
    OrderId: number;
    InvoiceId: number;
    InvoiceNumber: string;
    OrderCollectionNumber: string;
    OrderCollectionId: number;
    OrderNumber: number;
    CreatedAt: string;
    BrokerAgent: string;
    BrokerFirm: string;
    InvoiceReceiver: string;
    InvoiceEmail: string;
    Email: string;
    InvoiceAddress: string;
    InvoiceZip: string;
    InvoiceCity: string;
    InvoiceComment: string;
    InvoiceBillingMethod: number;
    Quantity: number;
    Price: number;
    TotalAmount: number;
    ProductName: string;
    AddressLine: string;
    AddressDistrict: string;
    AddressPostalNumber: string;
    AddressCity: string;
    AddressLine1: string;
    ProcountorId: string;
    InvoiceStatusValue: number;
    InvoiceStatusLabel: string;
    IsDefaultSelected: boolean;
    HasValidProcountorId: boolean;
    ValidationMessage: string;
    RowCsvLine: string;
}

export interface IProcountorInvoicePreviewGroup {
    GroupId: string;
    ProcountorId: string;
    BrokerFirm: string;
    InvoiceReceiver: string;
    InvoiceEmail: string;
    InvoiceAddress: string;
    InvoiceZip: string;
    InvoiceCity: string;
    RowCount: number;
    TotalAmount: number;
    InvalidRowCount: number;
    HasValidProcountorId: boolean;
    ValidationMessage: string;
    MasterCsvLine: string;
    ProcountorIdAssignedAtExport: boolean;
    DefaultSelectedRowCount: number;
    DefaultSelectedAmount: number;
    Rows: IProcountorInvoicePreviewRow[];
}

export interface IProcountorInvoicePreviewResponse {
    Groups: IProcountorInvoicePreviewGroup[];
    Totals: IProcountorInvoicePreviewTotals;
    HasValidationErrors: boolean;
    CanExport: boolean;
    InvalidRowCount: number;
}

export interface IZentuvoInvoiceSelectionRequest {
    From: string;
    To: string;
    Single: boolean;
    OrderRowIds: number[];
}

export interface IGenerateProcountorIdResponse {
    ProcountorId: string;
    RawResponse?: string;
}

export interface IContentProviderOption {
    value: number;
    label: string;
}

function pick(obj: any, keys: string[], defaultValue: any) {
    if (obj == null) {
        return defaultValue;
    }

    for (let index = 0; index < keys.length; index += 1) {
        const key = keys[index];
        if (obj[key] != null) {
            return obj[key];
        }
    }

    return defaultValue;
}

function normalizeGenerateProcountorIdResponse(response: any): IGenerateProcountorIdResponse {
    const procountorId = pick(response, ['ProcountorId', 'procountorId'], '');
    const rawResponse = pick(response, ['RawResponse', 'rawResponse'], '');

    return {
        ProcountorId: procountorId,
        RawResponse: rawResponse
    };
}

function normalizePreviewResponse(response: any): IProcountorInvoicePreviewResponse {
    const groups = pick(response, ['Groups', 'groups'], []);
    const totals = pick(response, ['Totals', 'totals'], {});

    return {
        Groups: (groups || []).map((group: any) => {
            const rows = pick(group, ['Rows', 'rows'], []);
            const normalizedRows = (rows || []).map((row: any) => ({
                OrderRowId: pick(row, ['OrderRowId', 'orderRowId'], 0),
                OrderId: pick(row, ['OrderId', 'orderId'], 0),
                InvoiceId: pick(row, ['InvoiceId', 'invoiceId'], 0),
                InvoiceNumber: pick(row, ['InvoiceNumber', 'invoiceNumber'], ''),
                OrderCollectionNumber: pick(row, ['OrderCollectionNumber', 'orderCollectionNumber'], ''),
                OrderCollectionId: pick(row, ['OrderCollectionId', 'orderCollectionId'], 0),
                OrderNumber: pick(row, ['OrderNumber', 'orderNumber'], 0),
                CreatedAt: pick(row, ['CreatedAt', 'createdAt'], ''),
                BrokerAgent: pick(row, ['BrokerAgent', 'brokerAgent'], ''),
                BrokerFirm: pick(row, ['BrokerFirm', 'brokerFirm'], ''),
                InvoiceReceiver: pick(row, ['InvoiceReceiver', 'invoiceReceiver'], ''),
                InvoiceEmail: pick(row, ['InvoiceEmail', 'invoiceEmail'], ''),
                Email: pick(row, ['Email', 'email'], ''),
                InvoiceAddress: pick(row, ['InvoiceAddress', 'invoiceAddress'], ''),
                InvoiceZip: pick(row, ['InvoiceZip', 'invoiceZip'], ''),
                InvoiceCity: pick(row, ['InvoiceCity', 'invoiceCity'], ''),
                InvoiceComment: pick(row, ['InvoiceComment', 'invoiceComment'], ''),
                InvoiceBillingMethod: pick(row, ['InvoiceBillingMethod', 'invoiceBillingMethod'], 0),
                Quantity: pick(row, ['Quantity', 'quantity'], 0),
                Price: pick(row, ['Price', 'price'], 0),
                TotalAmount: pick(row, ['TotalAmount', 'totalAmount'], 0),
                ProductName: pick(row, ['ProductName', 'productName'], ''),
                AddressLine: pick(row, ['AddressLine', 'addressLine'], ''),
                AddressDistrict: pick(row, ['AddressDistrict', 'addressDistrict'], ''),
                AddressPostalNumber: pick(row, ['AddressPostalNumber', 'addressPostalNumber'], ''),
                AddressCity: pick(row, ['AddressCity', 'addressCity'], ''),
                AddressLine1: pick(row, ['AddressLine1', 'addressLine1'], ''),
                ProcountorId: pick(row, ['ProcountorId', 'procountorId'], ''),
                InvoiceStatusValue: pick(row, ['InvoiceStatusValue', 'invoiceStatusValue'], 0),
                InvoiceStatusLabel: pick(row, ['InvoiceStatusLabel', 'invoiceStatusLabel'], ''),
                IsDefaultSelected: pick(row, ['IsDefaultSelected', 'isDefaultSelected'], false),
                HasValidProcountorId: pick(row, ['HasValidProcountorId', 'hasValidProcountorId'], false),
                ValidationMessage: pick(row, ['ValidationMessage', 'validationMessage'], ''),
                RowCsvLine: pick(row, ['RowCsvLine', 'rowCsvLine'], '')
            }));

            const brokerFirmValue = pick(group, ['BrokerFirm', 'brokerFirm'], '') ||
                (normalizedRows[0] && normalizedRows[0].BrokerFirm) ||
                '';

            return {
                GroupId: pick(group, ['GroupId', 'groupId'], ''),
                ProcountorId: pick(group, ['ProcountorId', 'procountorId'], ''),
                BrokerFirm: String(brokerFirmValue).trim(),
                InvoiceReceiver: pick(group, ['InvoiceReceiver', 'invoiceReceiver'], ''),
                InvoiceEmail: pick(group, ['InvoiceEmail', 'invoiceEmail'], ''),
                InvoiceAddress: pick(group, ['InvoiceAddress', 'invoiceAddress'], ''),
                InvoiceZip: pick(group, ['InvoiceZip', 'invoiceZip'], ''),
                InvoiceCity: pick(group, ['InvoiceCity', 'invoiceCity'], ''),
                RowCount: pick(group, ['RowCount', 'rowCount'], 0),
                TotalAmount: pick(group, ['TotalAmount', 'totalAmount'], 0),
                InvalidRowCount: pick(group, ['InvalidRowCount', 'invalidRowCount'], 0),
                HasValidProcountorId: pick(group, ['HasValidProcountorId', 'hasValidProcountorId'], false),
                ValidationMessage: pick(group, ['ValidationMessage', 'validationMessage'], ''),
                MasterCsvLine: pick(group, ['MasterCsvLine', 'masterCsvLine'], ''),
                ProcountorIdAssignedAtExport: pick(
                    group,
                    ['ProcountorIdAssignedAtExport', 'procountorIdAssignedAtExport'],
                    false
                ),
                DefaultSelectedRowCount: pick(group, ['DefaultSelectedRowCount', 'defaultSelectedRowCount'], 0),
                DefaultSelectedAmount: pick(group, ['DefaultSelectedAmount', 'defaultSelectedAmount'], 0),
                Rows: normalizedRows
            };
        }),
        Totals: {
            TotalRowCount: pick(totals, ['TotalRowCount', 'totalRowCount'], 0),
            TotalGroupCount: pick(totals, ['TotalGroupCount', 'totalGroupCount'], 0),
            TotalAmount: pick(totals, ['TotalAmount', 'totalAmount'], 0),
            DefaultSelectedRowCount: pick(totals, ['DefaultSelectedRowCount', 'defaultSelectedRowCount'], 0),
            DefaultSelectedAmount: pick(totals, ['DefaultSelectedAmount', 'defaultSelectedAmount'], 0)
        },
        HasValidationErrors: pick(response, ['HasValidationErrors', 'hasValidationErrors'], false),
        CanExport: pick(response, ['CanExport', 'canExport'], false),
        InvalidRowCount: pick(response, ['InvalidRowCount', 'invalidRowCount'], 0)
    };
}

export default class ZentuvoInvoicingApi {
    static getInvoiceDates(): Promise<string[]> {
        return new Api().get('/zentuvoinvoicing/invoice-dates').then((response) => {
            if (!Array.isArray(response)) {
                return [];
            }

            return response.map((value: any) => String(value));
        });
    }

    static getContentProviders(): Promise<IContentProviderOption[]> {
        return new Api().get('/zentuvoinvoicing/content-providers').then((response) => {
            if (!Array.isArray(response)) {
                return [];
            }

            return response.map((option: any) => ({
                value: Number(pick(option, ['Value', 'value'], 0)),
                label: String(pick(option, ['Label', 'label'], ''))
            }));
        });
    }

    static downloadInvoiceReportCsv(invoiceDates: string[], contentProviders: number[]): Promise<any> {
        const queryString = [
            ...invoiceDates.map((date) => `dates=${encodeURIComponent(date)}`),
            ...contentProviders.map((provider) => `contentProviders=${encodeURIComponent(String(provider))}`)
        ].join('&');

        return new Api().downloadCSVFile(`/zentuvoinvoicing/invoice-report-csv?${queryString}`);
    }

    static getPreview(fromDate: string, toDate: string, isDirect: boolean): Promise<IProcountorInvoicePreviewResponse> {
        return new Api()
            .post('/zentuvoinvoicing/preview', {
                From: fromDate,
                To: toDate,
                Single: isDirect
            })
            .then((response) => normalizePreviewResponse(response));
    }

    static exportToCSV(fromDate: string, toDate: string, isDirect: boolean): Promise<any> {
        return new Api().downloadCSVFile(`/zentuvoinvoicing/exporttocsv?from=${fromDate}&to=${toDate}&single=${isDirect}`);
    }

    static exportSelectedToCSV(request: IZentuvoInvoiceSelectionRequest): Promise<any> {
        return new Api().downloadFilePost('/zentuvoinvoicing/export-selected', request);
    }

    static updateStatus(request: IZentuvoInvoiceSelectionRequest, invoiceStatusValue: number): Promise<any> {
        return new Api().post('/zentuvoinvoicing/update-status', {
            From: request.From,
            To: request.To,
            Single: request.Single,
            OrderRowIds: request.OrderRowIds,
            InvoiceStatusValue: invoiceStatusValue
        });
    }

    static rollbackExportedInvoices(fromDate: string, toDate: string, isDirect: boolean): Promise<any> {
        return new Api().get(`/zentuvoinvoicing/rollbackexportedinvoices?from=${fromDate}&to=${toDate}&single=${isDirect}`);
    }

    static markExportedInvoicesAsFinished(fromDate: string, toDate: string, isDirect: boolean): Promise<any> {
        return new Api().get(`/zentuvoinvoicing/markexportedinvoicesasfinished?from=${fromDate}&to=${toDate}&single=${isDirect}`);
    }

    static rollbackFinishedToExported(fromDate: string, toDate: string, isDirect: boolean): Promise<any> {
        return new Api().get(`/zentuvoinvoicing/rollbackfinishedtoexported?from=${fromDate}&to=${toDate}&single=${isDirect}`);
    }

    static generateProcountorId(): Promise<IGenerateProcountorIdResponse> {
        return new Api()
            .post('/zentuvoinvoicing/generate-procountor-id', {})
            .then((response) => normalizeGenerateProcountorIdResponse(response));
    }
}